import React, { useEffect, useRef, useState } from "react";
import { Canvas, useLoader } from "@react-three/fiber";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import { useAnimations, Environment, ContactShadows } from "@react-three/drei";
import * as THREE from "three";
import HorizontalOrbitControls from "../orbit-controls";
import SpinnerLoader from "../model-loaders/spinner-loader";
import { AnimatePresence, motion } from "framer-motion";
import ModelCache from "../model-loaders/model-cache";
import { CustomIcon } from "../../../components/Icons";

interface Props {
  zoom?: number;
  model: {
    src: string;
    defaultScale?: number;
  };
  onLoad?: (model: Props["model"]) => void;
  onStart?: () => void;
}

// Custom hook to set up GLTF loader with decoders
const useGLTFWithDecoders = (url: string, onStart?: () => void, onLoad?: () => void, onError?: () => void) => {
  return useLoader(GLTFLoader, url, (loader) => {
    // Set up MeshoptDecoder for compressed files
    loader.setMeshoptDecoder(MeshoptDecoder);
    
    // Set up DRACOLoader for Draco compressed geometry
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
    dracoLoader.setDecoderConfig({ type: 'js' });
    loader.setDRACOLoader(dracoLoader);

    // Set up event handlers
    loader.manager.onStart = () => {
      if (onStart) onStart();
    };

    loader.manager.onLoad = () => {
      if (onLoad) onLoad();
    };

    loader.manager.onError = (error) => {
      console.error('GLTF loading error:', error);
      if (onError) onError();
    };
  });
};

const ModelPreview: React.FC<Props> = ({ model, onLoad, onStart }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const modelCache = ModelCache.getInstance();
  const modelRef = useRef<THREE.Group>(null);

  const { scene, animations } = useGLTFWithDecoders(
    model.src,
    () => {
      if (onStart) onStart();
      setIsLoading(true);
      setLoadError(false); // Reset error state on new load
    },
    () => {
      modelCache.markModelAsLoaded(model.src);
      setIsLoading(false);
      if (onLoad) {
        onLoad(model);
      }
    },
    () => {
      setIsLoading(false);
      setLoadError(true); // Handle loading error
    }
  );

  const { actions } = useAnimations(animations, modelRef);

  useEffect(() => {
    if (actions && animations.length > 0) {
      const actionName = animations[1]?.name || animations[0]?.name;
      if (actions[actionName]) {
        actions[actionName].play();
      }
    }
  }, [animations, actions]);

  const defaultScale = (model.defaultScale || 1) + 0.5;

  if (loadError) {
    return (
      <mesh>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color="red" />
      </mesh>
    );
  }

  return (
    <group position={[0, -1.4, 0]}>
      <primitive
        ref={modelRef}
        object={scene}
        position={[0, 0, 0]}
        receiveShadow
        castShadow
        scale={[defaultScale, defaultScale, defaultScale]}
      />
    </group>
  );
};

// Realistic Three-Point Lighting Setup
const RealisticLighting: React.FC = () => {
  return (
    <>
      {/* Key Light - Main directional light from top-front-left */}
      <directionalLight
        castShadow
        position={[5, 10, 5]}
        intensity={1.5}
        color="#ffffff"
        shadow-mapSize={[2048, 2048]}
        shadow-camera-far={50}
        shadow-camera-left={-10}
        shadow-camera-right={10}
        shadow-camera-top={10}
        shadow-camera-bottom={-10}
        shadow-bias={-0.0001}
      />
      
      {/* Fill Light - Softer light from the right to fill shadows */}
      <directionalLight
        position={[-3, 5, 2]}
        intensity={1.2}
        color="#b8d4ff"
      />
      
      {/* Rim Light - Back light for edge definition */}
      <directionalLight
        position={[-2, 3, -5]}
        intensity={0.8}
        color="#ffe4b8"
      />
      
      {/* Ambient Light - Soft overall illumination */}
      <ambientLight intensity={0.4} color="#f0f8ff" />
      
      {/* Point Light - Additional warm accent */}
      <pointLight
        position={[3, 2, 4]}
        intensity={1.5}
        color="#ffebb3"
        distance={20}
        decay={2}
      />
      
      {/* Hemisphere Light for natural sky/ground lighting */}
      <hemisphereLight
        intensity={0}
        args={["#87ceeb", "#8b7355", 0.6]}
      />
    </>
  );
};

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught an error", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-message fs-small py-3 gap-1 d-flex ">
          <CustomIcon
            icon="warning"
            className="scaleX"
            type="png"
            width={20}
            height={20}
          />{" "}
          Failed to load model. Please try again.
        </div>
      );
    }

    return this.props.children;
  }
}

const PreviewContainer: React.FC<Props> = ({ model, zoom }) => {
  const [modelLoaded, setModelLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const modelCache = ModelCache.getInstance();

  useEffect(() => {
    // Check if the model is already loaded in the cache
    if (modelCache.isModelLoaded(model.src)) {
      setModelLoaded(true);
      setLoadError(false); // Reset error state if model is cached
    } else {
      setModelLoaded(false);
    }
  }, [model, modelCache]);

  return (
    <ErrorBoundary>
      <Canvas 
        shadows={{ type: THREE.PCFSoftShadowMap }} 
        className="no-height" 
        camera={{ 
          zoom: zoom || 1.4,
          position: [0, 0, 5],
          fov: 70
        }}
        gl={{
          antialias: true,
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: 1.2,
        }}
      >
        <HorizontalOrbitControls autoRotate />
        
        {/* Realistic Lighting Setup */}
        <RealisticLighting />
        
        {/* Environment for realistic reflections */}
        {/* <Environment preset="studio" far={200} /> */}
        
        {/* Contact shadows for better ground interaction */}
        <ContactShadows
          position={[0, -1.4, 0]}
          opacity={0.4}
          scale={10}
          blur={2}
          far={4}
        />
        
        <ModelPreview
          model={model}
          onLoad={() => setModelLoaded(true)}
          onStart={() => {
            setLoadError(false); // Reset error state on load start
          }}
        />
      </Canvas>
      <AnimatePresence exitBeforeEnter>
        {(!modelLoaded || loadError) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.1 }}
            exit={{ opacity: 0 }}
          >
            {loadError ? (
              <div className="error-message">
                Failed to load model. Please try again.
              </div>
            ) : (
              <SpinnerLoader />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </ErrorBoundary>
  );
};

export default PreviewContainer;
